/**
 * ADR 189 · §5 y §6 — La ficha dice CÓMO llega, no de dónde viene.
 *
 * Al comprador no le importa la geografía: le importa si puede recogerla. Con
 * ~88% de los pedidos en recoger-en-tienda, ésa es la historia completa — y era
 * justo la que la ficha callaba.
 *
 * LA REGLA QUE ORDENA TODO: **la entrega encabeza; el lugar explica.** "Esta
 * carta está en Aguascalientes" como primera línea suena a disculpa y deja lo
 * accionable —cuánto tarda, si se recoge— de nota al pie. El lugar va abajo,
 * explicando por qué el plazo es ése. La única excepción es cuando el lugar no
 * es disculpa sino promesa (hay una copia en TU estado): ahí encabeza.
 *
 * De la regla sale la gramática de iconos, así no hay que memorizarla — **el
 * icono es el modo de entrega**: rayo = Express (lo pinta ADR 131, no este
 * archivo), tienda = recoges, caja = paquetería. El pin queda reservado para la
 * excepción, cuando el lugar encabeza.
 *
 * QUÉ NO HACE:
 *  - No adivina la región del comprador por IP (ADR 189 §6). En México el
 *    tráfico móvil sale por CDMX y Querétaro: equivocarse aquí es peor que no
 *    saber, porque le dirías "está en tu ciudad" a alguien a 500 km. Se sabe o
 *    no se sabe; cuando no se sabe hay un copy que es cierto para todos.
 *  - No promete nada sobre una carta sin datos de región. Sin dato, sin slot.
 *  - No pisa a Express: si el panel del rayo está visible, esta carta ya está
 *    en el punto y no hay nada que agregar (fila 1 de la tabla del ADR).
 *  - No habla de producto sellado. Ese filtro vive en Liquid (main-product):
 *    el sellado se asigna por variante directa y no pasa por el desempate
 *    regional, así que ahí no se renderiza slot y este archivo ni se entera.
 */
(function () {
  /* El plazo publicable. Sale del SLA del ADR 189 y es el mismo para recoger en
     un nodo de tu región, para envío dentro de la región que despacha y para el
     envío directo. Un solo lugar donde cambiarlo. */
  var SLA = '7 a 10 días';

  function medirUnaVez(evento, destino) {
    if (typeof window.vaultEvento !== 'function') return;
    var llave = evento + '|' + (destino || '');
    if (medidos[llave]) return;
    medidos[llave] = 1;
    window.vaultEvento(evento, destino || null);
  }
  var medidos = {};

  /* ───────────────────────── Iconos ─────────────────────────
     SVG de trazo con la gramática que ya usa el tema (lienzo de 24, trazo
     currentColor), dibujados a 16px. Nada de emoji: el emoji cambia de forma
     con el sistema operativo y no hereda el color del texto. */
  var ICONOS = {
    tienda:
      '<path d="M3 9.5 4.4 4h15.2L21 9.5"/><path d="M3 9.5h18v1.2a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z"/><path d="M4.6 13.4V20h14.8v-6.6"/>',
    caja: '<path d="M12 3 3.5 7.2v9.6L12 21l8.5-4.2V7.2Z"/><path d="M3.5 7.2 12 11.4l8.5-4.2"/><path d="M12 11.4V21"/>',
    pin: '<path d="M12 21s6.5-5.6 6.5-11a6.5 6.5 0 1 0-13 0c0 5.4 6.5 11 6.5 11Z"/><circle cx="12" cy="10" r="2.4"/>',
  };

  function icono(nombre) {
    var span = document.createElement('span');
    span.className = 'vault-entrega__icono';
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">' +
      (ICONOS[nombre] || '') +
      '</svg>';
    return span;
  }

  /* ───────────────────────── El calendario de la valija ─────────────────────
     ENCHUFE (queda pendiente de F3 · ADR 190 §1): la cadencia de la valija vive
     en configuración y `/api/regiones/estado` la va a exponer junto al mapa de
     regiones, con esta forma:

       "valija": { "cadencia": "quincenal", "proxima": "2026-09-08", "transito": 3 }

     Mientras ese dato NO exista, el estado de la valija no se publica: cae al
     copy de envío directo, que es cierto siempre. Prometer una corrida que no
     está calendarizada sería exactamente el error del ⚡ otra vez.

     Las dos cadencias no comparten copy a propósito (ADR 189): con corrida
     quincenal un plazo plano miente —quien compra al día siguiente esperaría
     casi 20 días— así que se publica la FECHA; cuando pase a semanal el error
     máximo baja a ±3 días y el copy se simplifica solo, sin reescribir nada. */
  function fechaLarga(iso, masDias) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return '';
    /* Mediodía UTC: leer 'YYYY-MM-DD' como medianoche UTC deja la fecha un día
       atrás al formatearla en horario de México. */
    var d = new Date(iso + 'T12:00:00Z');
    if (isNaN(d.getTime())) return '';
    if (masDias) d.setUTCDate(d.getUTCDate() + masDias);
    try {
      return new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City',
        weekday: 'long',
        day: 'numeric',
      }).format(d);
    } catch (e) {
      return '';
    }
  }

  function copyValija(valija, regionCarta) {
    if (!valija) return null;

    if (valija.cadencia === 'semanal') {
      return [
        'Está en ' + regionCarta + ': viaja en la valija de la semana y la recoges en 7 días.',
        'Sin esperar la valija: ' + SLA + '.',
      ];
    }

    var salida = fechaLarga(valija.proxima);
    var llegada = fechaLarga(valija.proxima, valija.transito == null ? 3 : valija.transito);
    if (!salida || !llegada) return null;
    return [
      'Está en ' +
        regionCarta +
        ': viaja en la valija del ' +
        salida +
        ' y la recoges alrededor del ' +
        llegada +
        '.',
      'Sin esperar la valija: ' + SLA + '.',
    ];
  }

  /* ───────────────────────── Los estados de la ficha ───────────────────────── */

  function nodosDe(regiones, estado) {
    var info = regiones && estado ? regiones[estado] : null;
    return info && info.nodos ? info.nodos : 0;
  }

  /** La región que despacha: se prefiere una con nodo, que es la que puede ofrecer recoger. */
  function regionQueDespacha(cartas, regiones) {
    for (var i = 0; i < cartas.length; i++) {
      if (nodosDe(regiones, cartas[i]) > 0) return cartas[i];
    }
    return cartas[0];
  }

  function recogeEnRegion(region, regiones) {
    var n = nodosDe(regiones, region);
    /* ENCHUFE: el ADR quiere aquí la lista real —"Hobby Mart · Anzures · 7 a 10
       días"—, pero `/api/regiones/estado` hoy sólo devuelve el CONTEO de nodos
       (`{hub, nodos, catalogo}`). Cuando F4 exponga nombre y colonia, esta línea
       se cambia por la lista y nada más de aquí se mueve. */
    var puntos = n === 1 ? '1 punto de entrega' : n + ' puntos de entrega';
    return {
      id: 'recoge-region',
      evento: 'entrega_recoge_region',
      icono: 'tienda',
      titular: 'Recógela sin costo en ' + region,
      detalle: [
        puntos + ' en ' + region + ' · ' + SLA,
        'O te la mandamos por paquetería en ' + SLA + '.',
      ],
    };
  }

  /**
   * La tabla del ADR 189 §5, en una sola función pura. Se expone abajo porque es
   * lo único que vale la pena probar de este archivo, y el tema no tiene build
   * donde correr pruebas de otra forma.
   *
   * ctx = { regionesCarta: [], regionComprador: '', regiones: {}, valija: {}|null }
   */
  function estadoDe(ctx) {
    var cartas = (ctx && ctx.regionesCarta) || [];
    if (!cartas.length) return null; /* sin dato no se promete nada */

    var rc = (ctx && ctx.regionComprador) || '';
    var regiones = (ctx && ctx.regiones) || null;

    /* Su región tiene una copia: la asignación se la va a dar a él (ADR 189 §3). */
    if (rc && cartas.indexOf(rc) !== -1) {
      if (nodosDe(regiones, rc) > 0) return recogeEnRegion(rc, regiones);
      /* LA EXCEPCIÓN de la regla: aquí el lugar no es disculpa, es promesa, y
         por eso encabeza — con el pin, que existe justo para este caso.

         Pero la promesa va CONDICIONADA, y la condición no es un tecnicismo: el
         allocator regional (ADR 189 §3, ya en main) resuelve la región del
         comprador del `shipping_address.province` de la orden, y con región
         desconocida vuelve a FIFO plano. La región del selector vive en el
         navegador y NO viaja a la orden. O sea: la copia local le toca si su
         dirección de envío es de ese estado, y eso lo decide él en el checkout.
         Decirlo en seco —"es la que te toca"— sería prometer por cuenta de un
         dato que todavía no existe cuando se lee la ficha. */
      return {
        id: 'local-sin-nodo',
        evento: 'entrega_local_sin_nodo',
        icono: 'pin',
        titular: 'Hay una copia en ' + rc,
        detalle: [
          'Si tu dirección de envío es de ' + rc + ', es la que te toca.',
          'Te llega por paquetería en ' + SLA + '.',
        ],
      };
    }

    var despacha = regionQueDespacha(cartas, regiones);

    /* No se sabe su región. El copy tiene que ser cierto para TODOS, y lo es:
       si donde está la carta hay dónde recogerla, se ofrece; si no, paquetería. */
    if (!rc) {
      if (nodosDe(regiones, despacha) > 0) return recogeEnRegion(despacha, regiones);
      return {
        id: 'paqueteria',
        evento: 'entrega_paqueteria',
        icono: 'caja',
        titular: 'Te llega por paquetería en ' + SLA,
        detalle: [],
      };
    }

    /* Se sabe su región y la carta no está ahí. Las dos salidas son espejo y se
       eligen con los nodos de la región DEL COMPRADOR: con punto puede esperar
       la valija y recoger sin pagar envío; sin punto sólo hay envío. */
    if (nodosDe(regiones, rc) > 0) {
      var valija = copyValija(ctx.valija, despacha);
      if (valija) {
        return {
          id: 'valija',
          evento: 'entrega_valija',
          icono: 'tienda',
          titular: 'Recógela sin costo en tu punto de ' + rc,
          alterna: { icono: 'caja', titular: 'O por envío directo — $200' },
          detalle: valija,
        };
      }
      /* Sin calendario de valija se cae a envío directo: un plazo real en vez de
         una corrida inventada. */
    }

    return {
      id: 'envio-otra-region',
      evento: 'entrega_envio_otra_region',
      icono: 'caja',
      titular: 'Te llega por envío en ' + SLA,
      detalle: [
        'Estás viendo todo el país: esta copia está en ' +
          despacha +
          ', no la recoges en ' +
          rc +
          '.',
      ],
    };
  }

  /* ───────────────────────── Pintar ───────────────────────── */

  function titular(texto, nombreIcono) {
    var fila = document.createElement('p');
    fila.className = 'vault-entrega__titular';
    fila.appendChild(icono(nombreIcono));
    var span = document.createElement('span');
    span.textContent = texto;
    fila.appendChild(span);
    return fila;
  }

  function pintar(slot, estado) {
    slot.textContent = '';
    slot.appendChild(titular(estado.titular, estado.icono));
    if (estado.alterna) {
      slot.appendChild(titular(estado.alterna.titular, estado.alterna.icono));
    }
    if (estado.detalle.length) {
      var p = document.createElement('p');
      p.className = 'vault-entrega__detalle';
      for (var i = 0; i < estado.detalle.length; i++) {
        if (i) p.appendChild(document.createElement('br'));
        p.appendChild(document.createTextNode(estado.detalle[i]));
      }
      slot.appendChild(p);
    }
    slot.hidden = false;
  }

  /**
   * ¿Express ya está diciendo lo suyo? Entonces esta carta ya está en el punto y
   * el slot de entrega sobra (fila 1 de la tabla). Ojo: el panel de Express
   * puede EXISTIR y estar oculto —su ventana de pickup sólo abre la víspera de
   * la visita (ADR 131)—, y en ese caso sí toca decir cómo llega hoy.
   */
  function expressVisible(slot) {
    var caja = slot.parentNode;
    if (!caja) return false;
    var paneles = caja.querySelectorAll('.vault-express');
    for (var i = 0; i < paneles.length; i++) {
      if (paneles[i].hidden) continue;
      if (paneles[i].querySelector('.vault-express__row:not([hidden])')) return true;
    }
    return false;
  }

  /** Acepta "CDMX|Jalisco" y "CDMX,Jalisco": el metafield de F1 puede llegar en cualquiera de las dos. */
  function listaDe(valor) {
    return (valor || '')
      .split(/[|,]/)
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function aplicar(payload) {
    var slots = document.querySelectorAll('[data-vault-entrega]');
    if (!slots.length) return;

    var regiones = payload ? payload.regiones : null;
    /* Ver el ENCHUFE de arriba: mientras `/api/regiones/estado` no traiga
       `valija`, esto es undefined y el estado de la valija no se publica. */
    var valija = payload ? payload.valija : null;
    var rc = window.vaultRegion ? window.vaultRegion.elegida() : '';

    for (var i = 0; i < slots.length; i++) {
      var slot = slots[i];

      if (slot.getAttribute('data-agotada') === '1' || expressVisible(slot)) {
        slot.hidden = true;
        slot.textContent = '';
        slot.dataset.firma = 'oculto';
        continue;
      }

      var estado = estadoDe({
        regionesCarta: listaDe(slot.getAttribute('data-regiones')),
        regionComprador: rc,
        regiones: regiones,
        valija: valija,
      });

      if (!estado) {
        slot.hidden = true;
        slot.dataset.firma = 'oculto';
        continue;
      }

      /* La firma corta el ciclo: pintar dentro del div del precio despierta al
         observer de Express, que a su vez puede despertar al nuestro. Si el
         estado no cambió, no se toca el DOM y la cadena se detiene sola. */
      var firma = estado.id + '|' + estado.titular + '|' + estado.detalle.join('¶');
      if (slot.dataset.firma === firma) continue;
      slot.dataset.firma = firma;

      pintar(slot, estado);
      medirUnaVez(estado.evento, slot.getAttribute('data-regiones') || '');
    }
  }

  function arrancar() {
    if (!document.querySelector('[data-vault-entrega]')) return;
    if (!window.vaultRegion) return; /* sin vault-region.js no hay niveles ni región elegida */

    window.vaultRegion.datos(function (payload) {
      aplicar(payload);

      /* Dawn reemplaza el div del precio al cambiar de variante, y con él se va
         el slot: nace vacío otra vez y hay que repintarlo. La región es un dato
         POR VARIANTE (ADR 189 §2), así que el estado puede cambiar de una
         opción a otra — no basta con pintarlo una vez. */
      if (!window.MutationObserver) return;
      var agendado = false;
      new MutationObserver(function () {
        if (agendado) return;
        agendado = true;
        setTimeout(function () {
          agendado = false;
          aplicar(payload);
        }, 50);
      }).observe(document.body, { childList: true, subtree: true });
    });
  }

  /* El núcleo probable, para el harness de node:vm — este repo no tiene build ni
     runner, y auditar los módulos del tema así es lo que ya hace el equipo. */
  window.vaultEntrega = { estadoDe: estadoDe };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
